import SEO from './components/SEO';

export default function TermsOfService() {
  return (
    <>
      <SEO 
        title="Terms of Service"
        description="Read our Terms of Service to understand the rules, rights, and responsibilities when using Capstone Pakistan's academic competition services."
        keywords="terms of service, user agreement, competition rules, academic competitions, Capstone Pakistan, student participation"
      />
      
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen py-16">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-lg text-gray-600">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8">
            
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms of Service ("Terms") govern your use of Capstone Pakistan's website, services, and academic competitions. By accessing our website or participating in our competitions, you agree to be bound by these Terms. If you disagree with any part of these terms, you may not access our services.
              </p>
            </section>

            {/* Definitions */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Definitions</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>"Service"</strong> refers to Capstone Pakistan's website and academic competition services</li>
                <li><strong>"User," "you," and "your"</strong> refers to students, parents, teachers, and educational institutions using our services</li>
                <li><strong>"We," "us," and "our"</strong> refers to Capstone Pakistan</li>
                <li><strong>"Competitions"</strong> refers to PMMC, WMTC, PGEO, and other academic competitions we organize</li>
                <li><strong>"Content"</strong> refers to text, images, videos, and other materials on our website</li>
              </ul>
            </section>

            {/* Eligibility */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Eligibility and Registration</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>You must be a student, parent, teacher, or educational institution representative to use our services</li>
                <li>Students under 18 must have parental consent to participate in competitions</li>
                <li>You must provide accurate and complete information during registration</li>
                <li>You are responsible for maintaining the confidentiality of your account information</li>
                <li>You must notify us immediately of any unauthorized use of your account</li>
              </ul>
            </section>

            {/* Competition Rules */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Competition Rules and Conduct</h2>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">General Rules</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                <li>All participants must follow the specific rules of each competition</li>
                <li>Cheating, plagiarism, or any form of academic dishonesty is strictly prohibited</li>
                <li>Participants must respect the time limits and submission deadlines</li>
                <li>Team competitions require collaboration and fair participation from all members</li>
                <li>Decisions made by competition judges and organizers are final</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Code of Conduct</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Treat all participants, organizers, and staff with respect and courtesy</li>
                <li>Maintain a positive and supportive learning environment</li>
                <li>Do not engage in harassment, bullying, or discriminatory behavior</li>
                <li>Follow all venue and online platform rules during competitions</li>
                <li>Report any violations or concerns to competition organizers</li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Intellectual Property Rights</h2>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">Our Content</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                All content on our website, including but not limited to text, graphics, logos, images, and software, is the property of Capstone Pakistan and is protected by copyright and other intellectual property laws.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Your Content</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                By submitting content to our competitions or website, you grant us a non-exclusive, royalty-free license to use, reproduce, and distribute your content for educational and promotional purposes.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Competition Materials</h3>
              <p className="text-gray-700 leading-relaxed">
                Competition problems, solutions, and materials remain the property of their respective creators and may not be reproduced or distributed without permission.
              </p>
            </section>

            {/* Prohibited Activities */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Prohibited Activities</h2>
              <p className="text-gray-700 leading-relaxed mb-4">You agree not to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Use our services for any illegal or unauthorized purpose</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Interfere with or disrupt the operation of our website or services</li>
                <li>Upload or transmit viruses, malware, or other harmful code</li>
                <li>Harass, abuse, or harm other users or staff members</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Use automated systems to access our services without permission</li>
                <li>Attempt to reverse engineer or copy our proprietary technology</li>
              </ul>
            </section>

            {/* Privacy and Data */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Privacy and Data Protection</h2>
              <p className="text-gray-700 leading-relaxed">
                Your privacy is important to us. Our collection and use of personal information is governed by our <a href="/privacy-policy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</a>. By using our services, you consent to the collection and use of your information as described in our Privacy Policy.
              </p>
            </section>

            {/* Disclaimers */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Disclaimers and Limitations</h2>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">Service Availability</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We strive to provide reliable and uninterrupted services, but we cannot guarantee that our website or services will always be available or error-free. We reserve the right to modify, suspend, or discontinue any part of our services at any time.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">Limitation of Liability</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the maximum extent permitted by law, Capstone Pakistan shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">No Warranty</h3>
              <p className="text-gray-700 leading-relaxed">
                Our services are provided "as is" without any warranties, express or implied. We do not warrant that our services will meet your specific requirements or be error-free.
              </p>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify and hold harmless Capstone Pakistan, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of our services or violation of these Terms.
              </p>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may terminate or suspend your access to our services immediately, without prior notice, for any reason, including breach of these Terms.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Upon termination, your right to use our services will cease immediately, and we may delete your account and related information.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Governing Law and Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of Pakistan. Any disputes arising from these Terms or your use of our services shall be resolved through negotiation, mediation, or arbitration in Pakistan.
              </p>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms is found to be unenforceable, the remaining provisions will continue to be valid and enforceable.
              </p>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to Terms of Service</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the updated Terms on our website. Your continued use of our services after such changes constitutes acceptance of the new Terms.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-gray-700 mb-2">
                  <strong>Email:</strong> <a href="mailto:admin@capstonepakistan.org" className="text-blue-600 hover:text-blue-800">admin@capstonepakistan.org</a>
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Organization:</strong> Capstone Pakistan
                </p>
                <p className="text-gray-700">
                  <strong>Address:</strong> Pakistan
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
} 